import { useEffect } from "react";

/**
 * Softens wheel scrolling on desktop (lerp + light damping).
 * Skips touch, reduced-motion, and nested overflow scroll areas.
 */
export default function useSmoothPageScroll(enabled) {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return undefined;

    let target = window.scrollY;
    let current = window.scrollY;
    let rafId = 0;
    let animating = false;

    const ease = 0.055;
    const wheelScale = 0.72;

    const maxScroll = () =>
      Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

    const clamp = (value) => Math.min(maxScroll(), Math.max(0, value));

    const tick = () => {
      const diff = target - current;
      if (Math.abs(diff) < 0.4) {
        current = target;
        window.scrollTo(0, current);
        animating = false;
        rafId = 0;
        return;
      }
      current += diff * ease;
      window.scrollTo(0, current);
      rafId = window.requestAnimationFrame(tick);
    };

    const start = () => {
      if (animating) return;
      animating = true;
      rafId = window.requestAnimationFrame(tick);
    };

    const isNestedScrollable = (el) => {
      let node = el;
      while (node && node !== document.body && node !== document.documentElement) {
        if (node instanceof HTMLElement) {
          const { overflowY } = window.getComputedStyle(node);
          if (
            (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
            node.scrollHeight > node.clientHeight + 1
          ) {
            return true;
          }
        }
        node = node.parentElement;
      }
      return false;
    };

    const onWheel = (event) => {
      if (event.ctrlKey) return;
      if (isNestedScrollable(event.target)) return;
      event.preventDefault();
      target = clamp(target + event.deltaY * wheelScale);
      start();
    };

    const onScroll = () => {
      if (animating) return;
      target = window.scrollY;
      current = window.scrollY;
    };

    const onResize = () => {
      target = clamp(target);
      current = clamp(current);
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [enabled]);
}
